import { afterAnimationEnd, afterCallstack, debounce } from './utils.js';

//+ Helper function to calculate scrollbar width
function getScrollbarWidth() {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll'; // forcing scrollbar
    document.body.appendChild(outer);

    // Creating inner element and placing it in the container
    const inner = document.createElement('div');
    outer.appendChild(inner);

    // Calculating difference between container's full width and the child's width
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

    // Removing temporary elements from the DOM
    outer.parentNode.removeChild(outer);

    return scrollbarWidth;
}

//+ Updating cart
document.addEventListener('cart:updated', (e) => {
    const itemCount = e.detail.cart.item_count;

    if (itemCount) {
        updateCartLength(itemCount);
    } else {
        fetch('/cart.js')
            .then((res) => res.json())
            .then((data) => updateCartLength(data.item_count));
    }
});

function updateCartLength(amount) {
    document.querySelectorAll('[data-cart-length]').forEach((el) => (el.textContent = amount));
}

//+ Lazy Resize

/**
 * Dispatches a custom 'lazy:resize' event.
 */
function dispatchLazyResizeEvent(e) {
    const event = new Event('lazy:resize', e);
    window.dispatchEvent(event);
}
/**
 * Dispatches a custom 'breakpoint:crossed' event when the window width crosses 850px.
 */
function dispatchBreakpointCrossedEvent() {
    const width = window.innerWidth;
    const isMobile = width <= 850;

    // Check if the breakpoint has been crossed
    if (window.isMobile !== isMobile) {
        window.isMobile = isMobile;
        const event = new CustomEvent('breakpoint:crossed', {
            detail: { width: width, isMobile: isMobile }
        });
        window.dispatchEvent(event);
    }
}

// Add a debounced event listener for the 'resize' event
window.addEventListener(
    'resize',
    debounce(() => {
        dispatchLazyResizeEvent();
        dispatchBreakpointCrossedEvent();
    }, 300)
);

dispatchBreakpointCrossedEvent();

//+ Script Loaded Event

function handleScriptLoad(event) {
    const scriptSrc = event.target.src;

    if (!scriptSrc || typeof scriptSrc !== 'string' || scriptSrc.trim() === '') {
        return;
    }

    try {
        const url = new URL(scriptSrc);
        const scriptName = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
        document.dispatchEvent(
            new CustomEvent('script:loaded', { detail: { src: scriptSrc, name: scriptName } })
        );
        document.dispatchEvent(
            new CustomEvent(`script:loaded:${scriptName}`, {
                detail: { src: scriptSrc, name: scriptName }
            })
        );
    } catch (e) {
        console.error(`handleScriptLoad: Failed to parse URL '${scriptSrc}'. Error:`, e, event.target);
    }
}

document.querySelectorAll('script').forEach((script) => {
    script.addEventListener('load', handleScriptLoad);
});

/* Lazy Init Script */

/**
 * Adds an observer to initialise a script when an element is scrolled into view.
 * @param {Element} element - Element to observe.
 * @param {Function} callback - Function to call when element is scrolled into view.
 * @param {number} [threshold=500] - Distance from viewport (in pixels) to trigger init.
 */
export function initLazyScript(element, callback, threshold = 500) {
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (typeof callback === 'function') {
                            afterCallstack(() => {
                                callback();
                                observer.unobserve(entry.target);
                            });
                        }
                    }
                });
            },
            { rootMargin: `0px 0px ${threshold}px 0px` }
        );

        io.observe(element);
    } else {
        afterCallstack(() => {
            callback(element);
        });
    }
}

window.initLazyScript = initLazyScript;

//+  Only open one details at a time

function initMaxOpenDetails() {
    const containers = document.querySelectorAll('[data-max-open-one]');

    containers.forEach((container) => {
        const detailsElements = container.querySelectorAll('details');

        detailsElements.forEach((details) => {
            details.addEventListener('toggle', () => {
                if (details.open) {
                    detailsElements.forEach((otherDetails) => {
                        if (otherDetails !== details) {
                            otherDetails.open = false;
                        }
                    });
                }
            });
        });
    });
}

document.addEventListener('DOMContentLoaded', initMaxOpenDetails);

//+  Close details on click outside

function initCloseOnUnfocus() {
    const details = document.querySelectorAll('[data-unfocus-close]');

    details.forEach((details) => {
        details.setAttribute('tabindex', '-1');

        details.addEventListener('focusout', (evt) => {
            setTimeout(() => {
                if (!details.contains(document.activeElement)) {
                    details.open = false;
                }
            }, 100);
        });
    });
}

document.addEventListener('DOMContentLoaded', initCloseOnUnfocus);

//+ External Links

function initExternalLinks() {
    document.addEventListener('click', (evt) => {
        const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
        if (link && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
            link.target = '_blank';
        }
    });
}

document.addEventListener('DOMContentLoaded', initExternalLinks);

//+ Anchor Scrolling

function initAnchorScrolling() {
    // Ensure anchor scrolling is smooth (this shouldn't be added in the CSS)
    document.addEventListener('click', (evt) => {
        if (
            evt.target.tagName === 'A' &&
            window.location.hostname === new URL(evt.target.href).hostname &&
            evt.target.href.includes('#')
        ) {
            document.getElementsByTagName('html')[0].style.scrollBehavior = 'smooth';
            setTimeout(() => {
                document.getElementsByTagName('html')[0].style.scrollBehavior = '';
            }, 1000);
        }
    });
}

document.addEventListener('DOMContentLoaded', initAnchorScrolling);

//! Lock Body Scroll

export function lockBodyScroll() {
    // Save current scroll position
    window.unlockedScrollY = window.scrollY;
    console.log('locking body scroll at', window.unlockedScrollY);

    // Check for iOS devices specifically
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    // Check for desktop (non-mobile) devices
    const isDesktop = window.innerWidth > 1024;
    
    // For non-iOS devices, handle scrollbar compensation
    if (!isIOS) {
        const scrollbarWidth = getScrollbarWidth();
        if (
            document.documentElement.scrollHeight > document.documentElement.clientHeight &&
            scrollbarWidth > 0
        ) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
            document.body.dataset.scrollbarPadding = scrollbarWidth;
        }
    }

    // iOS-specific approach
    if (isIOS) {
        document.body.classList.add('fixed');
        // Save current viewport height before we lock
        document.body.dataset.iosScrollLock = 'true'; 
        document.body.style.position = 'fixed';
        document.body.style.top = `-${window.unlockedScrollY}px`;
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        document.body.style.overflow = 'hidden';
    } 
    // Desktop devices - minimal locking to prevent keyboard issues
    else if (isDesktop) {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
    } 
    // Android and other mobile devices
    else {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
        document.body.classList.add('fixed');
        document.body.style.position = 'fixed';
        document.body.style.top = `-${window.unlockedScrollY}px`;
        document.body.style.width = '100%';
    }
}

export function unlockBodyScroll() {
    console.log('unlocking body scroll');

    // Check for iOS devices specifically
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const scrollY = window.unlockedScrollY || 0;

    // Clean up scrollbar padding if it was added
    if (document.body.dataset.scrollbarPadding) {
        document.body.style.paddingRight = '';
        delete document.body.dataset.scrollbarPadding;
    }

    // Restore overflow styles
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    
    // Clean up all fixed positioning properties
    document.body.style.top = '';
    document.body.style.width = '';
    document.body.style.height = '';
    document.body.style.position = '';
    document.body.classList.remove('fixed');
    
    // iOS specific cleanup
    if (document.body.dataset.iosScrollLock) {
        delete document.body.dataset.iosScrollLock;
        // For iOS, immediately scroll to the stored position
        window.scrollTo(0, scrollY);
    }
    // Other devices
    else if (typeof window.unlockedScrollY === 'number') {
        const isDesktop = window.innerWidth > 1024;
        // Only restore scroll on mobile non-iOS devices
        if (!isDesktop && !isIOS) {
            window.scrollTo(0, scrollY);
        }
    }
    
    // Always clean up the stored scroll position
    delete window.unlockedScrollY;
    
    // Force a small delay to ensure smooth transition back to scrollable state
    setTimeout(() => {
        // Trigger a small scroll to ensure iOS fully restores scrolling
        if (isIOS) {
            window.scrollTo(0, window.scrollY + 1);
        }
    }, 100);
}

//! Wait for DOM loaded

export function waitUntilDOMLoaded(callback) {
    return new Promise((resolve) => {
        if (document.readyState === 'complete') {
            // Check and add iOS class if needed
            detectIOSAndApplyClass();
            resolve(callback);
        } else {
            document.addEventListener('DOMContentLoaded', () => {
                // Check and add iOS class if needed
                detectIOSAndApplyClass();
                resolve(callback);
            });
        }
        
        // Helper function to detect iOS and add a class to the HTML element
        function detectIOSAndApplyClass() {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            if (isIOS) {
                document.documentElement.classList.add('is-ios');
            }
        }
    });
}
